<script>
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';
  import debounce from 'lodash/debounce';

  import { getContext } from 'svelte';

  import { productDataStore } from '../../stores/ProductData.js';
  import { webPSupport } from '../../stores/WebP.js';
  import { isMobile, designTabActive } from '../../stores/Designer.js';

  import ColoredButton from './ColoredButton.svelte';
  import DesignCard from './DesignCard.svelte';
  import FontOptions from './FontOptions.svelte';
  import FontColour from './FontColour.svelte';
  import Switch from '../Switch.svelte';
  import { previewImages } from '../../routes/design/[productSlug]/_previewImages.js';
  import PrintTypeSelection from './PrintTypeSelection.svelte';

  export let addToKonva;
  export let updateInKonva;
  export let editingElement;
  export let segment;

  const designerFonts = getContext('designerFonts');
  const heatTransferPrintColours = getContext('heatTransferPrintColours');

  let printOptions = $productDataStore[$productDataStore.showing].printOptions;

  let printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';

  // Background colour of selected product item - to show preview against
  let selectedProductColourMain = $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterMain;

  let selectedProductColourTrim1 =
    $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterTrim1;

  let selectedProductColourTrim2 =
    $productDataStore[$productDataStore.showing].selectedColour.ProductColoursMasterTrim2;

  let defaultPreviewImage;
  if (selectedProductColourMain.name === 'white') {
    defaultPreviewImage = previewImages.rosterBlack;
  } else {
    defaultPreviewImage = previewImages.rosterWhite;
  }

  let previewImage = defaultPreviewImage;
  let name;
  let number;
  let selectedType = 'name and number';
  let selectedStyle = 'name_top_straight';
  let sponsorship = false;

  let font = {
    fontFamily: {
      name: 'freshman',
      svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.52 19.85v-.58h-.6v2.39h1.7V25H4.96v-3.34h.89v-8.13h-.89V10.2h10.88v3.92h-3.33v-.59H9.92v2.42h.6v-.59h3.32v4.49h-3.32zm10.007.36v2.13h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h7.33l1.89 1.9v3.04l-.79.78.79.76v2.7h.71V25h-3.96v-4.4l-.37-.39h-1.64zm0-4.39v1.73h1.64l.37-.39v-.95l-.37-.39h-1.64zm10.897 4.6v1.92h2.08v-.48h2.66V25h-8.7v-2.66h.71v-6.52h-.71v-2.66h8.7v3.14h-2.66v-.48h-2.08v1.94h.48v-.48h2.66v3.62h-2.66v-.48h-.48zm11.48.41-.37-.39h-3.46l-1.92-1.88v-3.5l1.9-1.9h5.2l1.9 1.9v2.34h-3.25v-1.19l-.39-.39h-1.72l-.39.39v1.19l.39.38h3.46l1.9 1.9v3.42l-1.9 1.9h-5.2l-1.9-1.9v-2.27h3.25v1.12l.39.39h1.72l.39-.39v-1.12zm9.803 1.51h.73v-1.97h-2.33v1.97h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h4.67v2.66h-.71v1.64h2.33v-1.64h-.73v-2.66h4.69v2.66h-.71v6.52h.71V25h-4.69v-2.66zm14.804-3.07-2.59 4.27-2.58-4.27v3.07h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.8l2.74 4.6 2.75-4.6h3.81v2.66h-.73v6.52h.73V25h-4.68v-2.66h.71v-3.07zm7.755-6.11h6.28v2.66h-.6l2.61 6.52h.71V25h-4.56v-2.66h.4l-.17-.55h-3.14l-.2.55h.39V25h-4.53v-2.66h.71l2.61-6.52h-.51v-2.66zm3.09 4.51-.77 1.85h1.53l-.76-1.85zM96.046 25h-3.9l-3.2-5.29v2.63h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.86l3.24 5.42v-2.76h-.71v-2.66h4.67v2.66h-.71v6.52h.71V25z" transform="translate(-.503 -2.6)"/></svg>`,
    },
    fill: 'White',
    rgb: '(255, 255, 255)',
    text: '',
  };

  let fonts = {
    family: [],
    colour: [],
    rgb: [],
  };

  fonts.family = designerFonts.map((item) => {
    return { name: item.name, svg: item.svg };
  });

  fonts.colour = heatTransferPrintColours.map((item) => {
    return { name: item.colour, rgb: item.colourRgb };
  });

  font.fill = fonts.colour[0].name;
  font.rgb = fonts.colour[0].rgb;

  // Have a flag for loading of $editingElement
  // Turn off editing if transformer turns off?
  let editingElementLoaded = false;
  $: if ($editingElement && !editingElementLoaded) {
    // set all variables to values from editing element
    name = $editingElement.name;
    number = $editingElement.number;
    previewImage = $editingElement.image;

    selectedType = $editingElement.type;
    selectedStyle = $editingElement.style;
    sponsorship = $editingElement.sponsorship;
    printOption = $editingElement.printOption;

    // Need to run function to get svg that matches name
    let fontElement = fonts.family.find((x) => x.name === $editingElement.font);
    font.fontFamily.name = fontElement.name;
    font.fontFamily.svg = fontElement.svg;

    font.rgb = $editingElement.textColour.rgb;
    font.fill = $editingElement.textColour.name;

    editingElementLoaded = true;
  }

  // When transformer is detached editingElement becomes null
  // Need to reset variables to defaults in this scenario
  $: if (!$editingElement && editingElementLoaded) {
    // set variables back to defaults
    previewImage = defaultPreviewImage;

    font = {
      fontFamily: {
        name: 'freshman',
        svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.52 19.85v-.58h-.6v2.39h1.7V25H4.96v-3.34h.89v-8.13h-.89V10.2h10.88v3.92h-3.33v-.59H9.92v2.42h.6v-.59h3.32v4.49h-3.32zm10.007.36v2.13h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h7.33l1.89 1.9v3.04l-.79.78.79.76v2.7h.71V25h-3.96v-4.4l-.37-.39h-1.64zm0-4.39v1.73h1.64l.37-.39v-.95l-.37-.39h-1.64zm10.897 4.6v1.92h2.08v-.48h2.66V25h-8.7v-2.66h.71v-6.52h-.71v-2.66h8.7v3.14h-2.66v-.48h-2.08v1.94h.48v-.48h2.66v3.62h-2.66v-.48h-.48zm11.48.41-.37-.39h-3.46l-1.92-1.88v-3.5l1.9-1.9h5.2l1.9 1.9v2.34h-3.25v-1.19l-.39-.39h-1.72l-.39.39v1.19l.39.38h3.46l1.9 1.9v3.42l-1.9 1.9h-5.2l-1.9-1.9v-2.27h3.25v1.12l.39.39h1.72l.39-.39v-1.12zm9.803 1.51h.73v-1.97h-2.33v1.97h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h4.67v2.66h-.71v1.64h2.33v-1.64h-.73v-2.66h4.69v2.66h-.71v6.52h.71V25h-4.69v-2.66zm14.804-3.07-2.59 4.27-2.58-4.27v3.07h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.8l2.74 4.6 2.75-4.6h3.81v2.66h-.73v6.52h.73V25h-4.68v-2.66h.71v-3.07zm7.755-6.11h6.28v2.66h-.6l2.61 6.52h.71V25h-4.56v-2.66h.4l-.17-.55h-3.14l-.2.55h.39V25h-4.53v-2.66h.71l2.61-6.52h-.51v-2.66zm3.09 4.51-.77 1.85h1.53l-.76-1.85zM96.046 25h-3.9l-3.2-5.29v2.63h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.86l3.24 5.42v-2.76h-.71v-2.66h4.67v2.66h-.71v6.52h.71V25z" transform="translate(-.503 -2.6)"/></svg>`,
      },
      fill: 'White',
      rgb: '(255, 255, 255)',
      text: '',
    };
    name = '';
    number = '';
    let nameInput = document.querySelector('#nameInput');
    nameInput.value = '';
    let numberInput = document.querySelector('#numberInput');
    numberInput.value = null;
    selectedType = 'name and number';
    selectedStyle = 'name_top_straight';
    sponsorship = false;
    printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';
    // We've reset to default so mark editing loaded back to false so this block doesn't run again
    editingElementLoaded = false;
  }

  function setType(type) {
    selectedType = type;
  }

  function setStyle(style) {
    selectedStyle = style;
  }

  function generateRosterPreviewVariables(type, font, name, number, style, sponsorship) {
    // Do some checking for blank entries, etc
    if (type === 'name and number') {
      if (!name) {
        type = 'number';
      }
      if (!number) {
        type = 'name';
      }
    }

    let variables = {
      type: type,
      name: name,
      number: number ? number.toString() : '',
      font: font.fontFamily.name,
      textColour: font.rgb,
      style: style,
      sponsorship: sponsorship,
      webp: $webPSupport,
    };
    return variables;
  }

  function setPreviewImage(image) {
    previewImage = image;
    if ($editingElement) {
      addNameNumber();
    }
  }

  function determineValidInputs(type) {
    let valid = false;
    switch (type) {
      case 'name and number':
        if (name || number) {
          valid = true;
        }
        break;

      case 'name':
        if (name) {
          valid = true;
        }
        break;
      case 'number':
        if (number) {
          valid = true;
        }
        break;
    }
    return valid;
  }

  // Debounce updates on text input
  const updateNameNumber = debounce((event) => {
    let nameInput = document.querySelector('#nameInput');
    name = nameInput.value;
    let numberInput = document.querySelector('#numberInput');
    number = numberInput.value;
  }, 300);

  // Make a reactive statement to update preview image src. Need to use .then pattern as img generation is async
  $: imagePromise = updateRosterPreview(selectedType, font, name, number, selectedStyle, sponsorship, printOption).then(
    setPreviewImage,
  );

  async function updateRosterPreview(type, font, name, number, style, sponsorship) {
    if (typeof window !== 'undefined' && determineValidInputs(type)) {
      let variables = generateRosterPreviewVariables(type, font, name, number, style, sponsorship);
      let image = await generateRosterPreview(variables);
      return image;
    } else {
      // placeholder
      return defaultPreviewImage;
    }
  }

  async function generateRosterPreview(variables) {
    const IMAGE = gql`
      query GenerateRosterPreview(
        $font: String!
        $name: String!
        $number: String!
        $sponsorship: Boolean!
        $style: String!
        $textColour: String!
        $type: String!
        $webp: Boolean!
      ) {
        rosterPreview(
          font: $font
          name: $name
          number: $number
          sponsorship: $sponsorship
          style: $style
          textColour: $textColour
          type: $type
          webp: $webp
        ) {
          image
        }
      }
    `;

    const res = await client.query({
      query: IMAGE,
      variables: variables,
    });

    const data = await res.data;

    return data.rosterPreview.image;
  }

  async function addNameNumber() {
    updateNameNumber;
    const image = previewImage;

    let details = {
      printType: 'names & no',
      productType: $productDataStore.showing,
      segment: segment,
      type: selectedType,
      name: name,
      number: number?.toString(),
      style: selectedStyle,
      font: font.fontFamily.name,
      textColour: {
        name: font.fill,
        rgb: font.rgb,
      },
      webp: $webPSupport,
      sponsorship: sponsorship,
      printOption: printOption,
      image: image,
    };

    if ($editingElement) {
      updateInKonva($editingElement, image, details);
    } else {
      addToKonva(image, details);
      // reset defaults
      previewImage = defaultPreviewImage;

      font = {
        fontFamily: {
          name: 'freshman',
          svg: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 30"><path d="M10.52 19.85v-.58h-.6v2.39h1.7V25H4.96v-3.34h.89v-8.13h-.89V10.2h10.88v3.92h-3.33v-.59H9.92v2.42h.6v-.59h3.32v4.49h-3.32zm10.007.36v2.13h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h7.33l1.89 1.9v3.04l-.79.78.79.76v2.7h.71V25h-3.96v-4.4l-.37-.39h-1.64zm0-4.39v1.73h1.64l.37-.39v-.95l-.37-.39h-1.64zm10.897 4.6v1.92h2.08v-.48h2.66V25h-8.7v-2.66h.71v-6.52h-.71v-2.66h8.7v3.14h-2.66v-.48h-2.08v1.94h.48v-.48h2.66v3.62h-2.66v-.48h-.48zm11.48.41-.37-.39h-3.46l-1.92-1.88v-3.5l1.9-1.9h5.2l1.9 1.9v2.34h-3.25v-1.19l-.39-.39h-1.72l-.39.39v1.19l.39.38h3.46l1.9 1.9v3.42l-1.9 1.9h-5.2l-1.9-1.9v-2.27h3.25v1.12l.39.39h1.72l.39-.39v-1.12zm9.803 1.51h.73v-1.97h-2.33v1.97h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h4.67v2.66h-.71v1.64h2.33v-1.64h-.73v-2.66h4.69v2.66h-.71v6.52h.71V25h-4.69v-2.66zm14.804-3.07-2.59 4.27-2.58-4.27v3.07h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.8l2.74 4.6 2.75-4.6h3.81v2.66h-.73v6.52h.73V25h-4.68v-2.66h.71v-3.07zm7.755-6.11h6.28v2.66h-.6l2.61 6.52h.71V25h-4.56v-2.66h.4l-.17-.55h-3.14l-.2.55h.39V25h-4.53v-2.66h.71l2.61-6.52h-.51v-2.66zm3.09 4.51-.77 1.85h1.53l-.76-1.85zM96.046 25h-3.9l-3.2-5.29v2.63h.71V25h-4.67v-2.66h.71v-6.52h-.71v-2.66h3.86l3.24 5.42v-2.76h-.71v-2.66h4.67v2.66h-.71v6.52h.71V25z" transform="translate(-.503 -2.6)"/></svg>`,
        },
        fill: 'White',
        rgb: '(255, 255, 255)',
        text: '',
      };
      name = '';
      number = '';
      let nameInput = document.querySelector('#nameInput');
      nameInput.value = '';
      let numberInput = document.querySelector('#numberInput');
      numberInput.value = null;
      selectedType = 'name and number';
      selectedStyle = 'name_top_straight';
      sponsorship = false;
      printOption = printOptions[0]?.PrintOption?.name || 'digital transfer';
    }
  }
</script>

<section>
  <div class="preview">
    <img src={previewImage} alt="Text Preview" style="--bgColour: rgb{selectedProductColourMain.rgb}" />
  </div>
  <div class="section-label">Print Type</div>
  <div class="top">
    <DesignCard border={selectedType === 'name and number'} clickfn={() => setType('name and number')}>
      <div class="type">
        <img src="img/design/names/name-number.jpg" alt="" />
        <span class="label">Player Name & No</span>
      </div>
    </DesignCard>

    <DesignCard border={selectedType === 'name'} clickfn={() => setType('name')}>
      <div class="type">
        <img src="img/design/names/name-only.jpg" alt="" />
        <span class="label">Player Name</span>
      </div>
    </DesignCard>

    <DesignCard border={selectedType === 'number'} clickfn={() => setType('number')}>
      <div class="type">
        <img src="img/design/names/number-only.jpg" alt="" />
        <span class="label">Player Number</span>
      </div>
    </DesignCard>
  </div>

  <div class="input-container">
    <input
      id="nameInput"
      on:input={updateNameNumber}
      type="text"
      class:hidden={selectedType !== 'name' && selectedType !== 'name and number'}
      placeholder="Player Name"
      value={$editingElement ? $editingElement.name : ''}
      required
    />

    <input
      id="numberInput"
      on:input={updateNameNumber}
      type="number"
      class:hidden={selectedType !== 'number' && selectedType !== 'name and number'}
      placeholder="Player Number"
      value={$editingElement ? $editingElement.number : ''}
      required
    />
  </div>

  <div class="section-label">Print Style</div>
  <div class="style-container">
    <DesignCard clickfn={() => setStyle('name_top_straight')} border={selectedStyle === 'name_top_straight'}>
      <div class="style">
        <img src="img/design/names/name_top_straight.jpg" alt="" />
      </div>
    </DesignCard>
    <DesignCard clickfn={() => setStyle('name_top_curved_1')} border={selectedStyle === 'name_top_curved_1'}>
      <div class="style">
        <img src="img/design/names/name_top_curved_1.jpg" alt="" />
      </div>
    </DesignCard>
    <DesignCard clickfn={() => setStyle('name_top_curved_2')} border={selectedStyle === 'name_top_curved_2'}>
      <div class="style">
        <img src="img/design/names/name_top_curved_2.jpg" alt="" />
      </div>
    </DesignCard>
    <DesignCard clickfn={() => setStyle('name_bottom_straight')} border={selectedStyle === 'name_bottom_straight'}>
      <div class="style">
        <img src="img/design/names/name_bottom_straight.jpg" alt="" />
      </div>
    </DesignCard>
  </div>

  <div class="font-container">
    <div>
      <div class="section-label">Font Family</div>
      <FontOptions bind:options={fonts.family} bind:selected={font.fontFamily} />
    </div>
    <div>
      <div class="section-label">Text Colour</div>
      <FontColour bind:options={fonts.colour} bind:selected={font.fill} bind:selectedRgb={font.rgb} />
    </div>
  </div>

  {#if selectedType !== 'name' && segment !== 'front-print'}
    <div class="sponsorship-container">
      <div class="section-label">Add Sponsorship - 5% Discount</div>
      <Switch bind:value={sponsorship} label="" design="inner" labelTrue="Yes" labelFalse="No" />
      <!-- <div class="sponsorship-message">
      Add Uniform Hero sponsorship and receive 5% discount on your order.
    </div> -->
    </div>
  {/if}

  {#if printOptions.length > 1}
    <div class="print-option-container">
      <PrintTypeSelection {printOptions} bind:printOption />
    </div>
  {/if}

  <div class="note">
    <p>
      <b>NOTE:</b> This is a Placeholder Element. Each Jersey will be updated to show individual Player Names & Numbers as
      entered on the Team Details step!
    </p>

    <div class="grid">
      {#if !$editingElement}
        <ColoredButton label="+ Name/Number" type="green" clickFn={addNameNumber} />
      {/if}
      {#if $isMobile && $designTabActive}
        {#if $editingElement}
          <ColoredButton label="Update" type="green" clickFn={() => ($designTabActive = false)} />
        {/if}
        <ColoredButton label="Close" type="red" clickFn={() => ($designTabActive = false)} />
      {/if}
    </div>
  </div>
</section>

<style>
  section {
    margin: 0;
    padding: 2.2rem 0;
  }

  .close {
    margin-bottom: 1rem;
    width: fit-content;
    font-size: 2rem;
    font-weight: bold;
    color: #fe5900;
    margin-left: auto;
    margin-right: 0;
    box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.1);
    border-radius: 10rem;
    padding: 1rem 1.5rem;
    text-transform: uppercase;
  }

  .preview {
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
  }

  .preview img {
    background-color: var(--bgColour);
    padding: 1rem;
    max-height: 90%;
  }

  .top {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
  }

  .type {
    display: flex;
    flex-direction: column;
    height: 8rem;
    justify-content: center;
    align-items: center;
  }

  .type img {
    max-height: 100%;
    max-width: 100%;
    width: auto;
    height: auto;
  }

  .input-container {
    margin: 2rem 0;
  }

  .type .label {
    font-size: 1rem;
    margin-top: auto;
  }

  input {
    padding: 1rem 2rem;
    border: 2px dashed rgba(0, 0, 0, 0.2);
    width: 100%;
    font-size: 1.8rem;
    border-radius: 1.5rem;
    margin: 1rem 0;
    font-family: 'Inter', sans-serif;
  }

  input:focus {
    outline: none;
    border: 2px dashed #000;
  }

  .hidden {
    display: none;
  }

  .style-container {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-gap: 1rem;
  }

  .section-label {
    font-weight: bold;
    color: #666;
    letter-spacing: 1.5px;
    font-size: 1.2rem;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
    display: block;
  }

  .style img {
    width: 100%;
  }

  .font-container {
    margin-top: 2rem;
    margin-bottom: 2rem;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-gap: 1rem;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin: 2rem 0;
  }

  /* .sponsorship-message {
    font-size: 1.2rem;
    margin-top: 1.2em;
    padding-left: 1rem;
  } */

  .note p {
    color: #333;
    font-size: 1.4rem;
  }

  .print-option-container {
    clear: both;
    padding-top: 2rem;
  }
</style>
